"use client"

import React from "react"
import { useState, useEffect } from "react"
import Image from "next/image"
import { useRouter } from "next/navigation"
import { HiChevronLeft, HiChevronRight } from "react-icons/hi2"
import { apiGetAllNews } from '@/services/newsHome/api';
import { NewsItem } from '@/services/newsHome/type';

const getSourceInfo = (news: NewsItem) => {
    if (news.listRelatedNews && news.listRelatedNews.length > 0) {
        const sourceItem = news.listRelatedNews[0];
        return {
            name: sourceItem.title || "Rovi Holdings",
            logo: sourceItem.image || null
        };
    }
    return { name: "Tin tức", logo: null };
}

const PRESS_ID = Number(process.env.NEXT_PUBLIC_PRESS_ID);

export default function NewsRVHSection() {
    const [newsList, setNewsList] = useState<NewsItem[]>([])
    const [currentIndex, setCurrentIndex] = useState(0)
    const [isLoading, setIsLoading] = useState(true)
    const [direction, setDirection] = useState<'next' | 'prev'>('next')
    const router = useRouter()

    // State lưu vị trí bắt đầu vuốt
    const [touchStart, setTouchStart] = useState(0)
    // State lưu vị trí kết thúc vuốt
    const [touchEnd, setTouchEnd] = useState(0)
    // Ngưỡng khoảng cách để nhận diện thao tác vuốt
    const minSwipeDistance = 50

    // --- 1. Hàm tạo Slug chuẩn---
    const createSlug = (title: string) => {
        return title.toLowerCase()
            .trim()
            .replace(/[áàảãạăắằẳẵặâấầẩẫậ]/g, 'a')
            .replace(/[éèẻẽẹêếềểễệ]/g, 'e')
            .replace(/[iíìỉĩị]/g, 'i')
            .replace(/[óòỏõọôốồổỗộơớờởỡợ]/g, 'o')
            .replace(/[úùủũụưứừửữự]/g, 'u')
            .replace(/[ýỳỷỹỵ]/g, 'y')
            .replace(/đ/g, 'd')
            .replace(/[^a-z0-9-]/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');
    };

    useEffect(() => {
        const fetchNews = async () => {
            try {
                setIsLoading(true)
                const res = await apiGetAllNews({
                    keywords: "",
                    newsTypeId: PRESS_ID,
                    isPublic: "true",
                    page: 1,
                    pageSize: 100,
                })

                if (res?.isOK && res?.result?.items) {
                    setNewsList(res.result.items)
                    if (res.result.items.length > 0) {
                        setCurrentIndex(Math.floor(res.result.items.length / 2) > 1 ? 1 : 0)
                    }
                }
            } catch (error) {
                console.error("Failed to fetch news:", error)
            } finally {
                setIsLoading(false)
            }
        }

        fetchNews()
    }, [])

    // Hàm lấy danh sách item hiển thị xung quanh item trung tâm
    const getVisibleItems = () => {
        const len = newsList.length
        if (len === 0) return []

        const visible = []

        for (let i = -2; i <= 2; i++) {
            const index = (currentIndex + i + len * 100) % len
            const item = newsList[index]
            if (item) {
                visible.push({
                    ...item,
                    uniqueKey: `${item.id}-${i}-${currentIndex}`,
                    position: i
                })
            }
        }
        return visible
    }

    // Hàm xử lý chuyển sang item tiếp theo
    const handleNext = () => {
        if (newsList.length === 0) return
        setDirection('next')
        setCurrentIndex((prev) => (prev + 1) % newsList.length)
    }

    // Hàm xử lý chuyển về item trước đó
    const handlePrev = () => {
        if (newsList.length === 0) return
        setDirection('prev')
        setCurrentIndex((prev) => (prev - 1 + newsList.length) % newsList.length)
    }

    const handleItemClick = (newsId: number, newsTitle: string) => {
        const slugTitle = createSlug(newsTitle || '');
        router.push(`/news/${slugTitle}-${newsId}`)
    }

    // Hàm xử lý sự kiện bắt đầu vuốt
    const onTouchStart = (e: React.TouchEvent) => {
        setTouchEnd(0)
        setTouchStart(e.targetTouches[0].clientX)
    }

    // Hàm xử lý sự kiện đang vuốt
    const onTouchMove = (e: React.TouchEvent) => {
        setTouchEnd(e.targetTouches[0].clientX)
    }

    // Hàm xử lý kết thúc vuốt và xác định hướng vuốt
    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return

        const distance = touchStart - touchEnd
        const isLeftSwipe = distance > minSwipeDistance
        const isRightSwipe = distance < -minSwipeDistance

        if (isLeftSwipe) {
            handleNext()
        }
        if (isRightSwipe) {
            handlePrev()
        }
    }

    // Chuỗi class dùng chung cho nút điều hướng slider
    const navButtonClasses = `
        hidden md:flex items-center justify-center 
        w-9 h-9 2xl:w-12 2xl:h-12 min-[1920px]:w-16 min-[1920px]:h-16 rounded-full
        bg-transparent border border-black text-black
        transition-all duration-300
        hover:scale-110
        cursor-pointer z-40 absolute top-1/2 -translate-y-1/2
    `

    // Giao diện hiển thị trạng thái loading
    if (isLoading) {
        return <div className="h-[600px] w-full flex items-center justify-center bg-white text-gray-400">Loading news...</div>
    }

    // Điều kiện không render khi không có dữ liệu
    if (newsList.length === 0) {
        return null
    }

    return (
        <section className="py-8 md:py-12 xl:py-16 2xl:py-24 min-[1920px]:py-32 px-0 bg-white overflow-hidden w-full">
            {/* Khối định nghĩa animation cho hiệu ứng chuyển slide */}
            <style jsx>{`
                /* Desktop animations - cứng hơn, nhanh hơn */
                @keyframes slideInRightDesktop {
                    from { 
                        transform: translateX(10%);
                        opacity: 0.8;
                    }
                    to { 
                        transform: translateX(0);
                        opacity: 1;
                    }
                }
                @keyframes slideInLeftDesktop {
                    from { 
                        transform: translateX(-10%);
                        opacity: 0.8;
                    }
                    to { 
                        transform: translateX(0);
                        opacity: 1;
                    }
                }
                
                /* Mobile animations - mượt mà, chậm hơn */
                @keyframes slideInRightMobile {
                    0% { 
                        transform: translateX(8%) scale(0.95);
                        opacity: 0.7;
                    }
                    100% { 
                        transform: translateX(0) scale(1);
                        opacity: 1;
                    }
                }
                @keyframes slideInLeftMobile {
                    0% { 
                        transform: translateX(-8%) scale(0.95);
                        opacity: 0.7;
                    }
                    100% { 
                        transform: translateX(0) scale(1);
                        opacity: 1;
                    }
                }
                
                /* Mobile - hiệu ứng mượt */
                .animate-slide-next {
                    animation: slideInRightMobile 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
                }
                .animate-slide-prev {
                    animation: slideInLeftMobile 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
                }
                
                /* Desktop - hiệu ứng cứng hơn */
                @media (min-width: 768px) {
                    .animate-slide-next {
                        animation: slideInRightDesktop 0.4s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
                    }
                    .animate-slide-prev {
                        animation: slideInLeftDesktop 0.4s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
                    }
                }
            `}</style>

            <div className="w-full">

                {/* Khối hiển thị tiêu đề và mô tả section */}
                <div className="-mb-4 md:-mb-4 text-center px-4 max-w-[1100px] mx-auto">
                    <p className="text-[10px] xl:text-xs 2xl:text-sm min-[1920px]:text-base font-normal text-green-600 uppercase tracking-wider mb-2">
                        NHẬN XÉT – ĐÁNH GIÁ
                    </p>
                    <h2 className="text-2xl md:text-3xl xl:text-4xl 2xl:text-5xl min-[1920px]:text-6xl font-normal text-gray-900 mb-3">
                        Báo chí nói về Rovi Holdings
                    </h2>
                    <p className="text-xs md:text-sm xl:text-sm 2xl:text-base min-[1920px]:text-lg text-gray-500">
                        Được các công ty tăng trưởng nhanh nhất thế giới tin tưởng
                    </p>
                </div>

                {/* Khối bao slider hiển thị tin tức */}
                <div className="relative group/carousel flex justify-center items-center w-full">

                    {/* Nút điều hướng sang trái */}
                    <button
                        onClick={handlePrev}
                        className={`${navButtonClasses} left-2 md:left-4 xl:left-12 2xl:left-16 min-[1920px]:left-32`}
                    >
                        <HiChevronLeft className="w-5 h-5 2xl:w-6 2xl:h-6 min-[1920px]:w-8 min-[1920px]:h-8" />
                    </button>

                    {/* Khối container slider và xử lý sự kiện vuốt */}
                    <div
                        className="relative w-full overflow-hidden"
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEnd}
                    >
                        {/* Khối hiển thị danh sách các item trong slider */}
                        <div
                            key={currentIndex}
                            className={`
                                flex justify-center items-center gap-4 md:gap-5 xl:gap-6 2xl:gap-10 min-[1920px]:gap-16 pt-0 pb-0 w-full min-h-[420px] md:min-h-[450px] xl:min-h-[480px] 2xl:min-h-[600px] min-[1920px]:min-h-[850px]
                                ${direction === 'next' ? 'animate-slide-next' : 'animate-slide-prev'}
                            `}
                        >
                            {getVisibleItems().map((news: any) => {
                                const pos = news.position;
                                const isCenter = pos === 0;

                                const sourceInfo = getSourceInfo(news);
                                const imageUrl = news.image || "/images/placeholder.jpg";

                                return (
                                    <div
                                        key={news.uniqueKey}
                                        // Truyền cả ID và Title để tạo URL
                                        onClick={() => handleItemClick(news.id, news.title)}
                                        className={`
                                            flex-shrink-0 
                                            w-[240px] h-[320px] 
                                            md:w-[260px] md:h-[350px] 
                                            xl:w-[290px] xl:h-[390px] 
                                            2xl:w-[400px] 2xl:h-[520px] 
                                            min-[1920px]:w-[600px] min-[1920px]:h-[780px]
                                            relative rounded-xl overflow-hidden  bg-gray-900
                                            transition-all duration-500 ease-in-out cursor-pointer
                                            ${isCenter ? 'z-30' : 'z-10'}
                                            scale-100
                                        `}
                                    >
                                        <Image
                                            src={imageUrl || "/placeholder.svg"}
                                            alt={news.title}
                                            fill
                                            className="object-cover transition-all duration-500 brightness-100"
                                            sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw"
                                        />

                                        {/* Lớp overlay tạo hiệu ứng gradient phủ ảnh */}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent z-10" />

                                        {/* Khối hiển thị nội dung chữ trên ảnh */}
                                        <div className="absolute inset-0 p-4 md:p-5 xl:p-6 2xl:p-8 min-[1920px]:p-12 flex flex-col justify-end z-30">
                                            <div className="mb-1">
                                                <div className="inline-flex items-center gap-2 text-white">
                                                    {sourceInfo.logo && (
                                                        <div className="relative w-5 h-5 xl:w-7 xl:h-7 2xl:w-9 2xl:h-9 min-[1920px]:w-12 min-[1920px]:h-12 rounded-full overflow-hidden bg-white">
                                                            <img
                                                                src={sourceInfo.logo || "/placeholder.svg"}
                                                                alt="logo"
                                                                className="w-full h-full object-contain"
                                                            />
                                                        </div>
                                                    )}
                                                    <span className="text-[10px] xl:text-[11px] 2xl:text-sm min-[1920px]:text-lg font-light tracking-wide">
                                                        {sourceInfo.name}
                                                    </span>
                                                </div>
                                            </div>

                                            <h3 className="text-sm md:text-base xl:text-lg 2xl:text-2xl min-[1920px]:text-3xl font-medium text-white leading-tight mb-2 xl:mb-3 line-clamp-3">
                                                {news.title}
                                            </h3>

                                            <p className="text-gray-200 text-[10px] xl:text-xs 2xl:text-sm min-[1920px]:text-lg line-clamp-2 leading-relaxed font-light">
                                                {news.description || news.seoDescription || ""}
                                            </p>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>

                    {/* Nút điều hướng sang phải */}
                    <button
                        onClick={handleNext}
                        className={`${navButtonClasses} right-2 md:right-4 xl:right-12 2xl:right-16 min-[1920px]:right-32`}
                    >
                        <HiChevronRight className="w-5 h-5 2xl:w-6 2xl:h-6 min-[1920px]:w-8 min-[1920px]:h-8" />
                    </button>

                </div>
            </div>
        </section>
    )
}